import { isCanadianPostalCode } from "@swivl-mono/helpers";
import { ProfileCollectPayload } from "@swivl-mono/types";
import parsePhoneNumberFromString from "libphonenumber-js";
import { postcodeValidator } from "postcode-validator";

const VarKey = {
  Email: "User:Email",
  PhoneNumber: "PhoneNumber",
  PostalCode: "User:PostalCode",
  FirstName: "FirstName"
};

// Locale-independent error codes emitted by the validator. The consumer maps these to localized
// strings via t() so the validator stays pure and free of UI text.
export const PROFILE_VALIDATION_ERROR = {
  REQUIRED: "REQUIRED",
  INVALID_EMAIL: "INVALID_EMAIL",
  INVALID_PHONE: "INVALID_PHONE",
  INVALID_POSTAL_CODE: "INVALID_POSTAL_CODE",
  NAME_TOO_SHORT: "NAME_TOO_SHORT"
} as const;
export type ProfileValidationErrorCode = (typeof PROFILE_VALIDATION_ERROR)[keyof typeof PROFILE_VALIDATION_ERROR];

export interface ProfileCollectValidationError {
  [field: string]: ProfileValidationErrorCode;
}

// `originalPayload` is the pre-translation payload (when the message went through
// MessageTranslationService). We use its English labels for any label-based field detection so the
// validator keeps working when item.label has been localized.
export function ValidateProfileCollect(_payload: ProfileCollectPayload, originalPayload?: ProfileCollectPayload) {
  let validationErrors: ProfileCollectValidationError = {};
  let payload = { ..._payload };

  payload.items.forEach((item, index: number) => {
    const originalLabel = originalPayload?.items[index]?.label ?? item.label;
    if (item.required && (!item.value || item.value.length === 0)) {
      validationErrors[item.label] = PROFILE_VALIDATION_ERROR.REQUIRED;
      return;
    } else if (!item.required && (!item.value || item.value.length === 0)) {
      return;
    }

    if (item.id === VarKey.Email) {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!re.test(String(item.value).toLowerCase())) {
        validationErrors[item.label] = PROFILE_VALIDATION_ERROR.INVALID_EMAIL;
      } else {
        item.value = item.value;
      }
    }

    if (item.id === VarKey.PhoneNumber || originalLabel === "Phone") {
      if (payload.countryCodes) {
        let isValid = false;
        payload.countryCodes.forEach((country) => {
          const parsedPhoneNumber = parsePhoneNumberFromString(item.value!, country as any);
          if (!parsedPhoneNumber || !parsedPhoneNumber.number || !parsedPhoneNumber.isValid()) {
          } else {
            isValid = true;

            if (parsedPhoneNumber.number !== item.value) {
              item.value = parsedPhoneNumber.number;
            }
          }
        });

        if (!isValid) {
          validationErrors[item.label] = PROFILE_VALIDATION_ERROR.INVALID_PHONE;
        }
      } else {
        const parsedPhoneNumber = parsePhoneNumberFromString(item.value!, "US");
        if (!parsedPhoneNumber || !parsedPhoneNumber.number || !parsedPhoneNumber.isValid()) {
          validationErrors[item.label] = PROFILE_VALIDATION_ERROR.INVALID_PHONE;
        } else {
          if (parsedPhoneNumber.number !== item.value) {
            item.value = parsedPhoneNumber.number;
          }
        }
      }
    }

    if (item.id === VarKey.PostalCode) {
      if (payload.countryCodes) {
        let isValid = false;
        payload.countryCodes.forEach((country) => {
          if (country === "CA") {
            const { isCanadian, normalizedValue } = isCanadianPostalCode(item.value!);
            if (isCanadian) {
              isValid = true;
              payload.items[index].value = normalizedValue;
            }
          } else {
            if (postcodeValidator(item.value!, country)) {
              isValid = true;
            }
          }
        });
      } else {
        let isValid = false;
        const { isCanadian, normalizedValue } = isCanadianPostalCode(item.value!);
        console.log("isCanadian", isCanadian);
        console.log("normalizedValue", normalizedValue);
        if (postcodeValidator(item.value!, "US") || isCanadian || postcodeValidator(item.value! || "", "BB")) {
          isValid = true;
          if (isCanadian) {
            payload.items[index].value = normalizedValue;
          }
        }
        if (!isValid) {
          validationErrors[item.label] = PROFILE_VALIDATION_ERROR.INVALID_POSTAL_CODE;
        }
      }
    }

    if (item.id === "FirstName") {
      if (item.value!.length < 2) {
        validationErrors[item.label] = PROFILE_VALIDATION_ERROR.NAME_TOO_SHORT;
      }
    }
  });

  return { validationErrors, payload };
}
